import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useRef, useState } from 'react';
import type { OperationsApartmentTurnover } from '@/shared/api';
import { useApi, useApiProblem } from '@/shared/api';
import { useObjectScope } from '@/shared/object-scope';
import { HeuteBoardState } from './HeuteBoardState';
import { HeuteDetailPanel } from './HeuteDetailPanel';
import { HeuteInvalidDate } from './HeuteInvalidDate';
import { HeuteFact, HeuteHero } from './HeuteHero';
import { heuteDay, heuteFilterCounts, type HeuteFilter } from './heuteDayModel';
import { isFinalProblem } from './heuteProblem';
import { HeuteLanes } from './HeuteLanes';
import { HeuteTodos } from './HeuteTodos';
import {
  nowMinutesInTimeZone,
  parseOperationsDate,
  safeTimeZone,
  todayInTimeZone,
} from './operationsDate';
import { useOperationsRefresh } from './useOperationsRefresh';

const short = (time: string) => time.slice(0, 5);

/**
 * Heute (Board „Heute“): Wald-Kopf mit Datumsband, nummerierte Aufgaben aus den Hinweisen des
 * Servers, „Dein Tag im Haus“ als Bahnen je Apartment und die Tagesdetails als Panel. Gilt immer
 * für genau ein Objekt und seine lokale Zeit; aktualisiert sich jede Minute und beim Zurückkehren.
 */
export function OperationsBoard() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const { group } = useObjectScope();
  const timeZone = safeTimeZone(group?.timezone);
  const localToday = todayInTimeZone(timeZone);
  const date = parseOperationsDate(search.date, localToday);
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<HeuteFilter>('all');
  // An den Tag gebunden: kehrt derselbe Apartment an einem anderen Tag zurück, öffnet nichts von selbst.
  const [selected, setSelected] = useState<{ id: string; date: string } | null>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const board = useQuery({
    queryKey: ['operations', group?.id, date],
    queryFn: () => api.operations.day(group!.id, date),
    enabled: Boolean(group),
    staleTime: 60_000,
  });
  // Heute steht nicht in der URL: eine über Nacht offene Seite folgt so dem neuen Tag.
  const setDate = (next: string) =>
    void navigate({ to: '/', search: (next === localToday ? {} : { date: next }) as never });
  const refresh = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: ['operations', group?.id] }),
    [group?.id, queryClient],
  );
  useOperationsRefresh(refresh);

  const problem = useApiProblem(board.error);
  // Rechte entzogen oder Objekt weg: der alte Stand mit Gastdaten bleibt nicht stehen.
  const data = isFinalProblem(problem) ? undefined : board.data;
  const nowMinutes = nowMinutesInTimeZone(timeZone);
  const open = (propertyId: string, from?: HTMLElement) => {
    trigger.current = from ?? (document.activeElement as HTMLElement | null);
    setSelected({ id: propertyId, date });
  };
  const facts = data ? (
    <>
      <HeuteFact
        value={data.summary.departureApartmentCount}
        label={`Abreisen bis ${short(data.checkOutTime)}`}
      />
      <HeuteFact value={data.summary.inHouseApartmentCount} label="bleiben" />
      <HeuteFact
        value={data.summary.arrivalApartmentCount}
        label={`Anreisen ab ${short(data.checkInTime)}`}
      />
    </>
  ) : null;

  return (
    <div className="flex flex-col">
      <HeuteHero
        date={date}
        today={localToday}
        hour={Math.floor(nowMinutes / 60)}
        asOf={data ? formatTime(data.asOfUtc, data.timeZone) : null}
        facts={facts}
        onDateChange={setDate}
        onRefresh={refresh}
        refreshing={board.isFetching}
      />
      <div className="relative z-10 -mt-[84px] flex flex-col gap-5 px-2 md:px-5">
        <HeuteBoardState
          board={board}
          problem={problem}
          asOf={data ? formatTime(data.asOfUtc, data.timeZone) : null}
          onRetry={refresh}
        />
        <HeuteInvalidDate raw={search.date} />
        {data && (
          <>
            <HeuteTodos data={data} future={date > localToday} onOpenApartment={(id) => open(id)} />
            {data.isPartialView && (
              <p className="text-ink-soft text-sm">
                Sie sehen nur die Apartments in Ihrem Berechtigungsbereich.
              </p>
            )}
            <HeuteLanes
              day={heuteDay(data, filter)}
              counts={heuteFilterCounts(data)}
              filter={filter}
              onFilterChange={setFilter}
              nowMinutes={date === localToday ? nowMinutes : null}
              checkInTime={data.checkInTime}
              checkOutTime={data.checkOutTime}
              onOpen={(apartment: OperationsApartmentTurnover, from) =>
                open(apartment.propertyId, from)
              }
            />
          </>
        )}
      </div>
      {data && (
        <HeuteDetailPanel
          apartment={
            selected?.date === date
              ? (data.apartments.find((a) => a.propertyId === selected.id) ?? null)
              : null
          }
          checkInTime={data.checkInTime}
          checkOutTime={data.checkOutTime}
          date={data.date}
          travelGroups={data.travelGroups.filter((group) =>
            group.apartments.some((apartment) => apartment.propertyId === selected?.id),
          )}
          onClose={() => {
            setSelected(null);
            requestAnimationFrame(() => trigger.current?.focus());
          }}
        />
      )}
    </div>
  );
}

function formatTime(value: string, timeZone: string) {
  return new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', timeZone }).format(
    new Date(value),
  );
}
